/**
 * Group Orders Page
 *
 * Drives the group-order clock by hand. A group order expires 14 days after it is created,
 * holds its host's one active-order slot until then, and emails its host a day before the end
 * — so QA testing the reminder or the expired state, or an operator draining a backlog, needs
 * a way to move that clock.
 */

import { useState } from 'react';
import { z } from 'zod';
import { AlertTriangle, CalendarClock, Clock, Loader2, Mail, Timer } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
  useExpireGroupOrder,
  useRunExpirySweep,
  useRunHostReminderSweep,
  useShortenGroupOrderDeadline,
} from '@/hooks/use-group-orders';
import type {
  ExpirySweepSummary,
  GroupOrderSnapshot,
  HostReminderSweepSummary,
} from '@/types/group-order';

/** The same check the endpoint applies, so the button disables on exactly what it rejects. */
const groupOrderIdSchema = z.string().uuid();

const HOUR_MS = 60 * 60 * 1000;

/** `datetime-local` takes the browser's local wall time, minute precision. */
function toLocalInputValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Read at submit, not render: the field can go stale while the page sits open. */
function isInFuture(date: Date): boolean {
  return date.getTime() > Date.now();
}

function formatDeadline(iso: string | null): string {
  if (!iso) return '—';
  return `${new Date(iso).toLocaleString()} (${iso})`;
}

export function GroupOrdersPage() {
  const { toast } = useToast();

  const [groupOrderId, setGroupOrderId] = useState('');
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [expired, setExpired] = useState<GroupOrderSnapshot | null>(null);
  const [summary, setSummary] = useState<ExpirySweepSummary | null>(null);
  const [confirmExpire, setConfirmExpire] = useState(false);
  const [confirmSweep, setConfirmSweep] = useState(false);

  const [deadlineOrderId, setDeadlineOrderId] = useState('');
  const [deadlineInput, setDeadlineInput] = useState('');
  const [shortened, setShortened] = useState<GroupOrderSnapshot | null>(null);
  const [confirmDeadline, setConfirmDeadline] = useState(false);

  const [reminderSummary, setReminderSummary] = useState<HostReminderSweepSummary | null>(null);
  const [confirmReminder, setConfirmReminder] = useState(false);

  const expireMutation = useExpireGroupOrder();
  const sweepMutation = useRunExpirySweep();
  const deadlineMutation = useShortenGroupOrderDeadline();
  const reminderMutation = useRunHostReminderSweep();

  const trimmedId = groupOrderId.trim();
  const idIsValid = groupOrderIdSchema.safeParse(trimmedId).success;

  const trimmedDeadlineOrderId = deadlineOrderId.trim();
  const deadlineOrderIdIsValid = groupOrderIdSchema.safeParse(trimmedDeadlineOrderId).success;
  const newDeadline = deadlineInput ? new Date(deadlineInput) : null;
  const newDeadlineIsValid = newDeadline !== null && !Number.isNaN(newDeadline.getTime());

  const handleShortenDeadline = () => {
    setConfirmDeadline(false);
    setShortened(null);
    if (!newDeadline || !isInFuture(newDeadline)) {
      toast({
        title: 'That time has already passed',
        description: 'Pick a deadline in the future.',
        variant: 'destructive',
      });
      return;
    }
    deadlineMutation.mutate(
      { groupOrderId: trimmedDeadlineOrderId, deadlineAt: newDeadline.toISOString() },
      {
        onSuccess: order => {
          setShortened(order);
          const inReminderWindow =
            order.deadlineAt !== null &&
            new Date(order.deadlineAt).getTime() - Date.now() <= 24 * HOUR_MS;
          toast({
            title: 'Deadline moved',
            description: inReminderWindow
              ? "The order is inside the host reminder window. If its host hasn't been reminded yet, the reminder goes out within 5 minutes, or now with Send reminders."
              : 'The order stays open until its new deadline.',
          });
        },
        onError: error => {
          toast({
            title: 'Could not move this deadline',
            description: error.message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  const runReminderSweep = (dryRun: boolean) => {
    setConfirmReminder(false);
    setReminderSummary(null);
    reminderMutation.mutate(
      { dryRun },
      {
        onSuccess: result => {
          setReminderSummary(result);
          toast({
            title: dryRun ? 'Dry run complete' : 'Reminders sent',
            description: dryRun
              ? `${result.scanned} host(s) would be emailed. Nothing was sent.`
              : `${result.sent} reminder(s) queued.`,
          });
        },
        onError: error => {
          toast({
            title: 'Reminder run failed',
            description: error.message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleExpire = () => {
    setConfirmExpire(false);
    setExpired(null);
    expireMutation.mutate(
      { groupOrderId: trimmedId, leaveOpen },
      {
        onSuccess: order => {
          setExpired(order);
          toast({
            title: leaveOpen ? 'Deadline back-dated' : 'Group order expired',
            description: leaveOpen
              ? 'The order is still open, but its deadline has passed. Joins and submits are now refused.'
              : 'The order is closed. Guests see the closed-order screen and the host can start a new one.',
          });
        },
        onError: error => {
          toast({
            title: 'Could not expire this group order',
            description: error.message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  const runSweep = (dryRun: boolean) => {
    setConfirmSweep(false);
    setSummary(null);
    sweepMutation.mutate(
      { dryRun },
      {
        onSuccess: result => {
          setSummary(result);
          toast({
            title: dryRun ? 'Dry run complete' : 'Sweep complete',
            description: dryRun
              ? `${result.scanned} order(s) are past their deadline. Nothing was changed.`
              : `${result.expired} order(s) closed.`,
          });
        },
        onError: error => {
          toast({
            title: 'Sweep failed',
            description: error.message,
            variant: 'destructive',
          });
        },
      }
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Group Orders</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Expiry and reminder controls. An order expires 14 days after it is created; until then
          it holds its host&apos;s one active-order slot, and its host is emailed a day before
          the end.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5" />
            Expire one group order
          </CardTitle>
          <CardDescription>
            Back-dates the deadline and then closes the order through the same transition the
            sweep uses, so the result is indistinguishable from a real expiry. This cannot be
            undone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="groupOrderId">Group order ID</Label>
            <Input
              id="groupOrderId"
              value={groupOrderId}
              onChange={event => setGroupOrderId(event.target.value)}
              placeholder="6456bb81-87f6-4528-8ae4-2fee7ff908d4"
              autoComplete="off"
              spellCheck={false}
            />
            {trimmedId.length > 0 && !idIsValid && (
              <p className="text-sm text-destructive">That is not a valid UUID.</p>
            )}
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="leaveOpen"
              checked={leaveOpen}
              onCheckedChange={checked => setLeaveOpen(checked === true)}
            />
            <div className="space-y-1">
              <Label htmlFor="leaveOpen" className="font-normal">
                Back-date the deadline only, leave the order open
              </Label>
              <p className="text-sm text-muted-foreground">
                Reproduces the window between a deadline passing and the sweep running: joins and
                submits are refused while the order still reads open.
              </p>
            </div>
          </div>

          <Button
            onClick={() => setConfirmExpire(true)}
            disabled={!idIsValid || expireMutation.isPending}
          >
            {expireMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Expiring...
              </>
            ) : (
              <>
                <Clock className="mr-2 h-4 w-4" />
                {leaveOpen ? 'Back-date deadline' : 'Expire now'}
              </>
            )}
          </Button>

          {expired && (
            <Alert>
              <AlertTitle className="flex items-center gap-2">
                Result
                <Badge variant={expired.status === 'locked' ? 'destructive' : 'secondary'}>
                  {expired.status}
                </Badge>
              </AlertTitle>
              <AlertDescription>
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm mt-2">
                  <dt className="text-muted-foreground">Deadline</dt>
                  <dd>{formatDeadline(expired.deadlineAt)}</dd>
                  <dt className="text-muted-foreground">Closed at</dt>
                  <dd>{expired.lockedAt ?? 'still open'}</dd>
                  <dt className="text-muted-foreground">Version</dt>
                  <dd>{expired.version}</dd>
                </dl>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Expiry sweep
          </CardTitle>
          <CardDescription>
            Closes every order whose deadline has already passed. Run the dry run first — it
            reports how many orders are in scope without changing anything.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => runSweep(true)}
              disabled={sweepMutation.isPending}
            >
              {sweepMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Dry run
            </Button>
            <Button onClick={() => setConfirmSweep(true)} disabled={sweepMutation.isPending}>
              Run sweep
            </Button>
          </div>

          {summary && (
            <Alert variant={summary.errors > 0 ? 'destructive' : 'default'}>
              <AlertTitle>
                {summary.dryRun ? 'Dry run' : 'Sweep'}
                {summary.skippedDisabled && ' — group ordering is off for this tenant'}
                {summary.skippedLocked && ' — another run held the lock'}
              </AlertTitle>
              <AlertDescription>
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm mt-2">
                  <dt className="text-muted-foreground">Scanned</dt>
                  <dd>{summary.scanned}</dd>
                  <dt className="text-muted-foreground">Closed</dt>
                  <dd>{summary.expired}</dd>
                  <dt className="text-muted-foreground">Raced</dt>
                  <dd>{summary.raced} (host closed it first — normal)</dd>
                  <dt className="text-muted-foreground">Contended</dt>
                  <dd>{summary.contended} (busy; next run takes it — normal)</dd>
                  <dt className="text-muted-foreground">Errors</dt>
                  <dd>{summary.errors}</dd>
                  <dt className="text-muted-foreground">Remaining</dt>
                  <dd>{summary.remaining}</dd>
                </dl>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5" />
            Set an earlier deadline
          </CardTitle>
          <CardDescription>
            Moves an open order&apos;s deadline to a time you choose, without expiring it. Under 24
            hours from now puts the order in the host reminder window. The deadline can only move
            earlier, never later.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="deadlineGroupOrderId">Group order ID</Label>
            <Input
              id="deadlineGroupOrderId"
              value={deadlineOrderId}
              onChange={event => setDeadlineOrderId(event.target.value)}
              placeholder="6456bb81-87f6-4528-8ae4-2fee7ff908d4"
              autoComplete="off"
              spellCheck={false}
            />
            {trimmedDeadlineOrderId.length > 0 && !deadlineOrderIdIsValid && (
              <p className="text-sm text-destructive">That is not a valid UUID.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="newDeadline">New deadline (your local time)</Label>
            <Input
              id="newDeadline"
              type="datetime-local"
              value={deadlineInput}
              onChange={event => setDeadlineInput(event.target.value)}
              className="w-fit"
            />
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setDeadlineInput(toLocalInputValue(new Date(Date.now() + HOUR_MS)))}
              >
                In 1 hour
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setDeadlineInput(toLocalInputValue(new Date(Date.now() + 23 * HOUR_MS)))
                }
              >
                In 23 hours
              </Button>
            </div>
          </div>

          <Button
            onClick={() => setConfirmDeadline(true)}
            disabled={!deadlineOrderIdIsValid || !newDeadlineIsValid || deadlineMutation.isPending}
          >
            {deadlineMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Moving...
              </>
            ) : (
              <>
                <Clock className="mr-2 h-4 w-4" />
                Set deadline
              </>
            )}
          </Button>

          {shortened && (
            <Alert>
              <AlertTitle className="flex items-center gap-2">
                Result
                <Badge variant="secondary">{shortened.status}</Badge>
              </AlertTitle>
              <AlertDescription>
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm mt-2">
                  <dt className="text-muted-foreground">Deadline</dt>
                  <dd>{formatDeadline(shortened.deadlineAt)}</dd>
                  <dt className="text-muted-foreground">Version</dt>
                  <dd>{shortened.version}</dd>
                </dl>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Host reminder emails
          </CardTitle>
          <CardDescription>
            Emails the host of every open order whose deadline is less than 24 hours away, once
            per order. This runs on its own every 5 minutes; these buttons run it now. The dry run
            reports who would be emailed without sending anything.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => runReminderSweep(true)}
              disabled={reminderMutation.isPending}
            >
              {reminderMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Dry run
            </Button>
            <Button onClick={() => setConfirmReminder(true)} disabled={reminderMutation.isPending}>
              Send reminders
            </Button>
          </div>

          {reminderSummary && (
            <Alert variant={reminderSummary.errors > 0 ? 'destructive' : 'default'}>
              <AlertTitle>
                {reminderSummary.dryRun ? 'Dry run' : 'Reminder run'}
                {reminderSummary.skippedDisabled && ' — group ordering is off for this tenant'}
                {reminderSummary.skippedLocked && ' — another run held the lock'}
              </AlertTitle>
              <AlertDescription>
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-sm mt-2">
                  <dt className="text-muted-foreground">In the window</dt>
                  <dd>{reminderSummary.scanned}</dd>
                  <dt className="text-muted-foreground">Sent</dt>
                  <dd>{reminderSummary.sent}</dd>
                  <dt className="text-muted-foreground">Raced</dt>
                  <dd>{reminderSummary.raced} (left the window first — normal)</dd>
                  <dt className="text-muted-foreground">Errors</dt>
                  <dd>{reminderSummary.errors}</dd>
                  <dt className="text-muted-foreground">Remaining</dt>
                  <dd>{reminderSummary.remaining}</dd>
                  {reminderSummary.sampleIds.length > 0 && (
                    <>
                      <dt className="text-muted-foreground">Orders</dt>
                      <dd className="font-mono break-all">
                        {reminderSummary.sampleIds.join(', ')}
                      </dd>
                    </>
                  )}
                </dl>
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmExpire} onOpenChange={setConfirmExpire}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {leaveOpen ? 'Back-date this deadline?' : 'Expire this group order?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {leaveOpen ? (
                <>
                  The deadline on <span className="font-mono">{trimmedId}</span> moves into the
                  past. Guests can no longer join or submit, and the original deadline is not
                  recoverable.
                </>
              ) : (
                <>
                  <span className="font-mono">{trimmedId}</span> closes. Guests see the
                  closed-order screen and can no longer submit; the host can start a new order.
                </>
              )}
              <br />
              <strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={expireMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExpire}
              disabled={expireMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {leaveOpen ? 'Back-date deadline' : 'Expire now'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmSweep} onOpenChange={setConfirmSweep}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Close every overdue group order?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {summary?.dryRun && (
                <>
                  The last dry run found <strong>{summary.scanned}</strong> order(s) past their
                  deadline
                  {summary.remaining > 0
                    ? `, with ${summary.remaining} more beyond the batch cap`
                    : ''}
                  . Running the sweep closes them.
                </>
              )}
              {summary && !summary.dryRun && (
                <>
                  This closes every order whose deadline has passed, across the whole tenant. The
                  last run was a real sweep, so those numbers are spent — run a fresh dry run to
                  see what is still outstanding.
                </>
              )}
              {!summary && (
                <>
                  This closes every order whose deadline has passed, across the whole tenant. No
                  dry run has been done in this session — run one first to see the blast radius.
                </>
              )}
              <br />
              <strong>This action cannot be undone.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sweepMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runSweep(false)}
              disabled={sweepMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Run sweep
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDeadline} onOpenChange={setConfirmDeadline}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Move this deadline earlier?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The deadline on <span className="font-mono">{trimmedDeadlineOrderId}</span> moves to{' '}
              <strong>{newDeadline && newDeadlineIsValid ? newDeadline.toLocaleString() : '—'}</strong>
              . It can only move earlier, so the original deadline cannot be restored.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deadlineMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleShortenDeadline}
              disabled={deadlineMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Set deadline
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmReminder} onOpenChange={setConfirmReminder}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Email every host due a reminder?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {reminderSummary?.dryRun ? (
                <>
                  The last dry run found <strong>{reminderSummary.scanned}</strong> host(s) due a
                  reminder. Sending emails them now.
                </>
              ) : (
                <>
                  This emails the host of every open order ending within 24 hours that has not been
                  reminded, across the whole tenant. Run a dry run first to see how many.
                </>
              )}
              <br />
              <strong>Emails cannot be unsent.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reminderMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runReminderSweep(false)}
              disabled={reminderMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Send reminders
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/**
 * Export as named component for router
 */
export const Component = GroupOrdersPage;
